import React, { useState } from "react";
import { MENU } from "../../data/menu";
import { useLanguage } from "../../i18n/LanguageContext";
import { whatsappUrl, reservationMessage } from "../../lib/whatsapp";
import { MessageCircle, ArrowRight } from "lucide-react";

export default function Carta() {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState(MENU[0].id);
  const active = MENU.find((c) => c.id === activeId) || MENU[0];
  const waReservation = whatsappUrl(reservationMessage(t));

  return (
    <section
      id="carta"
      data-testid="carta-section"
      className="relative py-24 md:py-32 border-t border-[#E5E0D8] bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">{t.carta.eyebrow}</div>
            <h2
              data-testid="carta-title"
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A1A1A]"
            >
              {t.carta.title}
            </h2>
            <p className="mt-5 text-[#5C5C5C] leading-relaxed max-w-2xl">
              {t.carta.subtitle}
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <a
              href={waReservation}
              target="_blank"
              rel="noreferrer"
              data-testid="carta-reserve-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white px-7 py-4 text-xs tracking-[0.2em] uppercase font-semibold transition-colors group"
            >
              <MessageCircle className="h-4 w-4" />
              {t.whatsapp.buttonLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 border-y border-[#E5E0D8] py-5 mb-12"
          data-testid="carta-tabs"
        >
          {MENU.map((cat) => {
            const isActive = cat.id === activeId;
            return (
              <button
                key={cat.id}
                data-testid={`carta-tab-${cat.id}`}
                onClick={() => setActiveId(cat.id)}
                className={`px-4 md:px-5 py-2.5 text-[0.7rem] md:text-xs tracking-[0.2em] uppercase font-semibold transition-colors ${
                  isActive
                    ? "bg-[#1A1A1A] text-white"
                    : "text-[#1A1A1A] hover:bg-[#1A1A1A]/5 border border-[#E5E0D8]"
                }`}
              >
                {t.carta.categories[cat.id]}
              </button>
            );
          })}
        </div>

        {/* Active category content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Sticky image */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="relative overflow-hidden">
              <img
                key={active.image}
                src={active.image}
                alt={t.carta.categories[active.id]}
                data-testid={`carta-image-${active.id}`}
                className="w-full h-[360px] md:h-[460px] lg:h-[540px] object-cover transition-opacity duration-700"
                style={{ animation: "fadeUp 0.8s ease both" }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 md:p-8">
                <div className="text-[#E8A87C] text-[0.65rem] tracking-[0.25em] uppercase mb-2">
                  {t.carta.label}
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-white">
                  {t.carta.categories[active.id]}
                </h3>
              </div>
            </div>
            {active.note && (
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">
                {active.note}
              </p>
            )}
          </div>

          {/* Price list */}
          <div className="lg:col-span-7">
            {/* Column headers */}
            <div className="hidden md:grid grid-cols-12 gap-4 pb-4 border-b border-[#1A1A1A]/15">
              <div className="col-span-7 text-[0.65rem] uppercase tracking-[0.25em] font-bold text-[#5C5C5C]">
                {t.carta.dish}
              </div>
              <div className="col-span-2 text-right text-[0.65rem] uppercase tracking-[0.25em] font-bold text-[#5C5C5C]">
                {t.carta.half}
              </div>
              <div className="col-span-3 text-right text-[0.65rem] uppercase tracking-[0.25em] font-bold text-[#5C5C5C]">
                {t.carta.full}
              </div>
            </div>

            <ul data-testid={`carta-list-${active.id}`} className="divide-y divide-[#E5E0D8]">
              {active.items.map((item, idx) => (
                <li
                  key={idx}
                  data-testid={`carta-item-${active.id}-${idx}`}
                  className="py-5 grid grid-cols-12 gap-3 md:gap-4 items-baseline"
                >
                  <div className="col-span-12 md:col-span-7">
                    <div className="font-serif-display text-lg md:text-xl text-[#1A1A1A] leading-tight">
                      {item.name}
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-2 md:text-right font-mono text-sm text-[#5C5C5C]">
                    {item.half ? (
                      <>
                        <span className="md:hidden text-[0.6rem] uppercase tracking-[0.2em] text-[#5C5C5C] mr-2">
                          {t.carta.half}
                        </span>
                        {item.half}
                      </>
                    ) : (
                      <span className="text-[#C6C0B5]">—</span>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-3 md:text-right font-mono text-base font-semibold text-[#8B1E0F]">
                    <span className="md:hidden text-[0.6rem] uppercase tracking-[0.2em] text-[#5C5C5C] mr-2 font-sans">
                      {t.carta.full}
                    </span>
                    {item.full}
                  </div>
                </li>
              ))}
            </ul>

            {/* Footnote */}
            <div className="mt-10 pt-6 border-t border-[#E5E0D8] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-xs text-[#5C5C5C] leading-relaxed">
                {t.carta.disclaimer}
              </div>
              <a
                href="tel:+34671712335"
                data-testid="carta-call-cta"
                className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B1E0F] hover:text-[#6A160A] whitespace-nowrap"
              >
                {t.common.callNow}: 671 71 23 35 →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
